import React from 'react';

interface CostCalculatorInputsProps {
  inputTokens: number;
  setInputTokens: (value: number) => void;
  outputTokens: number;
  setOutputTokens: (value: number) => void;
  numberOfCalls: number;
  setNumberOfCalls: (value: number) => void;
}

const CostCalculatorInputs: React.FC<CostCalculatorInputsProps> = ({
  inputTokens,
  setInputTokens,
  outputTokens,
  setOutputTokens,
  numberOfCalls,
  setNumberOfCalls,
}) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Calculate Your AI API Costs</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Input Tokens per Request
          </label>
          <input
            type="range"
            min="100"
            max="10000000"
            step="100"
            value={inputTokens}
            onChange={(e) => setInputTokens(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between items-center mt-2">
            <input
              type="number"
              value={inputTokens}
              onChange={(e) => setInputTokens(Number(e.target.value))}
              className="border border-gray-300 text-gray-900 px-3 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <span className="text-gray-600 ml-2 text-sm whitespace-nowrap">
              ({(inputTokens / 1000000).toFixed(3)}M)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Approximate words: {Math.round(inputTokens * 0.75)}
          </p>
        </div>

        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Output Tokens per Request
          </label>
          <input
            type="range"
            min="100"
            max="10000000"
            step="100"
            value={outputTokens}
            onChange={(e) => setOutputTokens(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between items-center mt-2">
            <input
              type="number"
              value={outputTokens}
              onChange={(e) => setOutputTokens(Number(e.target.value))}
              className="border border-gray-300 text-gray-900 px-3 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <span className="text-gray-600 ml-2 text-sm whitespace-nowrap">
              ({(outputTokens / 1000000).toFixed(3)}M)
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Approximate words: {Math.round(outputTokens * 0.75)}
          </p>
        </div>

        <div>
          <label className="block text-gray-700 mb-2 font-medium">
            Number of API Requests
          </label>
          <input
            type="range"
            min="1"
            max="100000"
            step="1"
            value={numberOfCalls}
            onChange={(e) => setNumberOfCalls(Number(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          <div className="flex justify-between items-center mt-2">
            <input
              type="number"
              value={numberOfCalls}
              onChange={(e) => setNumberOfCalls(Math.max(1, Number(e.target.value)))}
              className="border border-gray-300 text-gray-900 px-3 py-2 rounded-lg w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
            <span className="text-gray-600 ml-2 text-sm whitespace-nowrap">requests</span>
          </div>
          <p className="text-xs text-gray-500 mt-1">
            Total tokens: {((inputTokens + outputTokens) * numberOfCalls / 1000000).toFixed(2)}M
          </p>
        </div>
      </div>
      
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-sm text-gray-700">
          <strong>Tip:</strong> Not sure about token counts? A typical ChatGPT conversation uses ~500 input tokens and ~300 output tokens. 
          A code generation request might use ~200 input and ~800 output tokens.
        </p>
      </div>
    </div>
  );
};

export default CostCalculatorInputs;
